import { useState, useEffect, useMemo } from "react";
import { Form } from "react-bootstrap";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import swal from "sweetalert";
import Select from "react-select";
import {
  currencyList,
  formatToTwoDecimal,
  getCurrencySymbol,
  parseNumber,
} from "../../../../utils/numberFormatter";
import BASE_URL from "../../../../assets/global/url";
import useDecodeToken from "../../../../hooks/customHook/useDecodeToken";
import { Modal, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  useInvoicePDF,
  InvoicePDFFrame,
} from "../../Sub Folder/InvoicePDF2";
import NumberInDecimals from "../../../../components/inputs/NumberFormatted";
import { warnIfNoCutoff } from "../../../../utils/cutoffDateValidation";
import useDarkMode from "../../../../components/Themes/Usedarkmode";
import DatePicker from "react-datepicker";
import { format, parseISO, isValid as isValidDateFns } from "date-fns";
import TypedDateInput, {
  DATE_INPUT_FORMAT,
} from "../../../../components/inputs/TypedDateInput";

const tableHoverCSS = `
  .reopen-orig-table tbody tr:hover td,
  .reopen-new-table  tbody tr:hover td {
    background-color: #FFF9E6 !important;
  }
  /* Dark mode: the light yellow above turned hovered rows white-ish. Keep a
     faint amber tint on a dark surface instead. */
  [data-theme="dark"] .reopen-orig-table tbody tr:hover td,
  [data-theme="dark"] .reopen-new-table  tbody tr:hover td {
    background-color: rgba(240, 180, 60, 0.12) !important;
  }
`;

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "";

const arrowColor = (orig, next) => {
  const diff = parseNumber(next) - parseNumber(orig);
  if (diff > 0) return "#3B9F3F";
  if (diff < 0) return "#dc3545";
  return "#6c757d";
};

const recomputeSubtotal = (item) => {
  const raw = parseNumber(item.unitPrice) * parseNumber(item.quantity);
  const disc = parseNumber(item.discountItem);
  return item.discountType === "percentage"
    ? raw - (raw * disc) / 100
    : raw - disc;
};

const InvoiceUpdateReOpen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const userLoggedID = useDecodeToken();
  const { themeSelectStyles } = useDarkMode();
  const [validated, setValidated] = useState(false);

  const [transactionId, setTransactionId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [customerLabel, setCustomerLabel] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [selectedInvoiceDate, setSelectedInvoiceDate] = useState("");
  const [selectedDueDate, setSelectedDueDate] = useState("");
  const [destination, setDestination] = useState("");
  const [currencyId, setCurrencyId] = useState("");
  const [currencyRate, setCurrencyRate] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [drNumber, setDrNumber] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [containerNumber, setContainerNumber] = useState("");
  const [pier, setPier] = useState("");
  const [remarks, setRemarks] = useState("");
  const [amount, setAmount] = useState("");
  const [qty, setQty] = useState("");
  const [itemDiscount, setItemDiscount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [transactionDiscount, setTransactionDiscount] = useState(0);
  const [vatPercent, setVatPercent] = useState(0);
  const [selectedReopenDate, setSelectedReopenDate] = useState("");
  const [selectedTaxId, setSelectedTaxId] = useState("");
  const [selectedTaxRate, setSelectedTaxRate] = useState(0);
  const [taxName, setTaxName] = useState("");
  const [taxSettings, setTaxSettings] = useState([]);
  const [originalTaxRate, setOriginalTaxRate] = useState(0);
  const [originalTaxId, setOriginalTaxId] = useState("");

  // Special Tax States
  const [isSpecialTax, setIsSpecialTax] = useState(false);
  const [originalIsSpecialTax, setOriginalIsSpecialTax] = useState(false);
  const [backupVatPercent, setBackupVatPercent] = useState(0);
  const [backupTaxId, setBackupTaxId] = useState("");
  const [backupTaxRate, setBackupTaxRate] = useState(0);

  const [preVatBase, setPreVatBase] = useState(0);
  const [displayWeighingFee, setDisplayWeighingFee] = useState(0);
  const [displayOtherFee, setDisplayOtherFee] = useState(0);
  const [inputWeighingFee, setInputWeighingFee] = useState("");
  const [weighingFeeModal, setWeighingFeeModal] = useState(false);
  const [originalWeighingFee, setOriginalWeighingFee] = useState(0);
  const [originalOtherFee, setOriginalOtherFee] = useState(0);

  // Named Other Fees (multi-row, child table)
  const [otherFees, setOtherFees] = useState([]);
  const [totalFees, setTotalFees] = useState(0);
  const OTHER_FEE_PAGE_SIZE = 5;
  const [otherFeePage, setOtherFeePage] = useState(0);
  const [originalOtherFees, setOriginalOtherFees] = useState([]);

  // Stores the saved vatPercent so we can detect changes and reset on Cancel
  const [originalVatPercent, setOriginalVatPercent] = useState(0);
  const [originalInvoiceTotal, setOriginalInvoiceTotal] = useState(0);
  const [reopenBasedInvoiceDate, setReopenBasedInvoiceDate] = useState("");
  const [isViewingExisting, setIsViewingExisting] = useState(false);
  const [edit, setEdit] = useState(false);

  const [items, setItems] = useState([]);
  const [newItems, setNewItems] = useState([]);

  const [warehouseData, setWarehouseData] = useState([]);
  const [currencyData, setCurrencyData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [print, setPrint] = useState(false);
  // Generated once each time the PDF modal opens, shared by viewer and download
  const invoicePdf = useInvoicePDF(id, print);
  const [invoiceStatus, setInvoiceStatus] = useState("");
  const [isCutoffPosted, setIsCutoffPosted] = useState(false);
  const [isCutoffExists, setIsCutoffExists] = useState(true);
  const [transactionIdDisplay, setTransactionIdDisplay] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [selectedWarehouse, setSelectedWarehouse] = useState("");
  const [originalInvoiceDate, setOriginalInvoiceDate] = useState("");
  const openPDFPreview = () => setPrint(true);

  // ── Fetch invoice data ────────────────────────────────────────────────────
  useEffect(() => {
    if (!id) return;
    const fetchInvoice = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(
          `${BASE_URL}/invoice/fetchInvoiceById/${id}`,
        );
        const inv = data.invoice;
        const originalItemsRaw = data.originalItems;

        setCustomerId(inv.customer_id ?? "");
        setWarehouseId(inv.warehouse_id ?? "");
        setPaymentMethod(inv.payment_method ?? "");
        setSelectedInvoiceDate(inv.invoice_date ?? "");
        setSelectedDueDate(inv.due_date ?? "");
        setDestination(inv.destination ?? "");
        setCurrencyId(inv.currency_id ?? "");
        setCurrencyRate(inv.rate ?? "");
        setPaymentTerms(inv.payment_terms ?? "");
        setDrNumber(inv.dr_number ?? "");
        setPoNumber(inv.po_number ?? "");
        setContainerNumber(inv.container_number ?? "");
        setPier(inv.pier ?? "");
        setRemarks(inv.remarks ?? "");
        setAmount(inv.amount ?? "");
        setQty(inv.quantity ?? "");
        setTransactionDiscount(inv.transaction_discount ?? 0);
        setItemDiscount(inv.item_discount ?? 0);
        setTotalAmount(inv.total_amount ?? 0);
        setOriginalInvoiceDate(inv.invoice_date ?? "");
        const parsedVat = parseNumber(inv.vat_percent);
        setVatPercent(parsedVat);
        setOriginalVatPercent(parsedVat); // ← save baseline for change-detection & Cancel reset
        const parsedTaxRate = parseNumber(inv.tax_rate);
        const taxId = inv.tax_id || "";
        setSelectedTaxRate(parsedTaxRate);
        setOriginalTaxRate(parsedTaxRate);
        setSelectedTaxId(taxId);
        setOriginalTaxId(taxId);
        setTaxName(inv.tax_setting?.name || "");
        setIsSpecialTax(inv.isSpecialTax || false);
        setOriginalIsSpecialTax(inv.isSpecialTax || false);
        const parsedWeighingFee = parseNumber(inv.weighing_fee);
        setDisplayWeighingFee(parsedWeighingFee);
        setOriginalWeighingFee(parsedWeighingFee);

        // Load named other fees from child table
        const otherFeesList = data.other_fees_list || [];
        if (Array.isArray(otherFeesList)) {
          setOtherFees(otherFeesList);
          setOriginalOtherFees(otherFeesList.map((f) => ({ ...f })));
        }
        // Derive displayOtherFee from child table total
        const derivedOtherFee = (data.other_fees_list || []).reduce(
          (sum, f) => sum + parseNumber(f.feeAmount),
          0,
        );
        setDisplayOtherFee(derivedOtherFee);
        setOriginalOtherFee(derivedOtherFee);
        setSelectedReopenDate(inv.invoice_date || "");

        setInvoiceStatus(inv.status ?? "");
        setTransactionIdDisplay(
          inv.client_transaction_id ?? inv.transaction_id ?? "",
        );
        setSelectedCurrency(inv.currency_id ?? "");
        setSelectedWarehouse(inv.warehouse_id ?? "");

        setIsCutoffPosted(data.isPosted ?? false);
        setIsCutoffExists(data.cutoffExists ?? true);
        const origInvDate = data.originalInvoiceDate ?? null;
        if (inv.isReOpen && inv.reopen_sales_invoice_id) {
          // Use the original invoice's date as the validation base
          setReopenBasedInvoiceDate(origInvDate || inv.invoice_date || "");
        } else {
          // For create mode (no existing re-open), use the current invoice date
          setReopenBasedInvoiceDate(inv.invoice_date || "");
        }

        const c = inv.customer;
        if (c) {
          setCustomerLabel(
            c.type === "company"
              ? c.company_name || "--"
              : `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim() || "--",
          );
        }

        const mapItems = (sourceItems) =>
          (sourceItems ?? []).map((item) => {
            const sm = item.stock_management;
            const pl = sm?.product_list;
            return {
              id: item.id,
              stockManagementId: item.stock_management_id,
              productId: pl?.product_id ?? "--",
              productCode: pl?.product_code ?? "--",
              productName: pl?.product_name ?? "--",
              remaining: sm?.stock ?? "--",
              unitPrice: item.unit_price ?? 0,
              averagePrice: item.average_price ?? 0,
              quantity: item.quantity ?? 0,
              moisture: item.moisture ?? 0,
              netWeight: item.net_weight ?? 0,
              discountItem: item.discount_item ?? 0,
              discountType: item.discount_type ?? "fixed",
              subtotal: item.subtotal ?? 0,
            };
          });

        const reopenMapped = mapItems(inv.sales_invoice_tag_inventories);

        if (originalItemsRaw && originalItemsRaw.length > 0) {
          const originalMapped = mapItems(originalItemsRaw);
          setItems(originalMapped);
          setNewItems(reopenMapped.map((i) => ({ ...i })));
          setIsViewingExisting(true);

          if (inv.reopen_sales_invoice_id) {
            try {
              const { data: origData } = await axios.get(
                `${BASE_URL}/invoice/fetchInvoiceById/${inv.reopen_sales_invoice_id}`,
              );
              setOriginalInvoiceTotal(origData.invoice?.total_amount ?? 0);
            } catch {
              setOriginalInvoiceTotal(0);
            }
          }
        } else {
          setItems(reopenMapped);
          setNewItems(reopenMapped.map((i) => ({ ...i })));
          setIsViewingExisting(false);
          setOriginalInvoiceTotal(inv.total_amount ?? 0);
        }
      } catch (err) {
        console.error("Error fetching invoice:", err);
        swal({ icon: "error", title: "Failed to load invoice." });
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [id]);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/invoice/reOpenTransactionId`)
      .then((r) => setTransactionId(r.data))
      .catch(console.error);
    axios
      .get(`${BASE_URL}/invoice/getWarehouse`)
      .then((r) => setWarehouseData(r.data))
      .catch(console.error);
    axios
      .get(`${BASE_URL}/currency/fetchCurrency`)
      .then((r) => setCurrencyData(r.data))
      .catch(console.error);
    axios
      .get(`${BASE_URL}/invoice/getActiveSalesTax`)
      .then((r) => setTaxSettings(r.data))
      .catch(console.error);
  }, []);

  // ── Item update helper ────────────────────────────────────────────────────
  const updateNewItem = (index, field, value) => {
    setNewItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        const next = { ...item, [field]: value };
        if (field === "quantity") next.netWeight = value;
        next.subtotal = recomputeSubtotal(next);
        return next;
      }),
    );
  };

  // ── Derived values ────────────────────────────────────────────────────────
  const currencySymbol = getCurrencySymbol(currencyData, currencyId);
  const shipmentAmount = parseNumber(amount) * parseNumber(qty);

  const calcTotals = (arr) => {
    const totalQty = arr.reduce((s, i) => s + parseNumber(i.quantity), 0);
    const totalMoistQty = arr.reduce((s, i) => s + parseNumber(i.moisture), 0);
    const totalNetQty = arr.reduce((s, i) => s + parseNumber(i.netWeight), 0);
    const totalRaw = arr.reduce((s, i) => s + parseNumber(i.subtotal), 0);
    const moistAmount = arr.reduce((s, i) => {
      const r = parseNumber(i.moisture) / (parseNumber(i.quantity) || 1);
      return s + parseNumber(i.subtotal) * r;
    }, 0);
    const netAmount = totalRaw - moistAmount;
    return {
      totalQty,
      totalMoistQty,
      totalNetQty,
      totalRaw,
      moistAmount,
      netAmount,
    };
  };

  const orig = calcTotals(items);
  const next = calcTotals(newItems);

  const origSubtotal =
    orig.netAmount -
    parseNumber(itemDiscount) -
    parseNumber(transactionDiscount);
  const newSubtotal =
    next.netAmount -
    parseNumber(itemDiscount) -
    parseNumber(transactionDiscount);
  const origTotal = isViewingExisting
    ? parseNumber(originalInvoiceTotal)
    : parseNumber(totalAmount);

  // Correct base for VAT and WHT
  const computedPreVatBase = newSubtotal;
  const vatAmount =
    !isSpecialTax && vatPercent > 0
      ? (vatPercent / 100) * computedPreVatBase
      : 0;
  const taxAmount =
    !isSpecialTax && selectedTaxRate > 0
      ? (selectedTaxRate / 100) * computedPreVatBase
      : 0;

  // Special Tax Computation
  const specialTax = isSpecialTax ? computedPreVatBase * 0.1088 : 0;

  // Non-taxable fees: create_invoice.jsx DEDUCTS both weighing fee and other
  // fees from the total (see calculateTotals: "- wFee - oFee - namedFees").
  // Both are switched to match that convention.
  const wFee = parseNumber(displayWeighingFee);
  // displayOtherFee is only a derived snapshot of the named-fee total taken
  // at load time (see "Derive displayOtherFee from child table total" above).
  // It is NOT a separate fee — namedFees below already covers it — so it must
  // not be included in newTotal or it double-counts every named other fee.
  const namedFees = otherFees.reduce(
    (sum, fee) => sum + (parseNumber(fee.feeAmount) || 0),
    0,
  );

  const newTotal =
    computedPreVatBase + vatAmount - taxAmount + specialTax - wFee - namedFees;
  const difference = newTotal - origTotal;

  // ── Change detection ──────────────────────────────────────────────────────
  // Returns true when any editable field differs from the saved baseline.
  // Covers: vatPercent, unitPrice, quantity, moisture, netWeight, discountItem.
  const hasChanges = useMemo(() => {
    if (items.length === 0) return false;
    if (vatPercent !== originalVatPercent) return true;
    if (selectedTaxRate !== originalTaxRate) return true;
    if (isSpecialTax !== originalIsSpecialTax) return true;
    if (displayWeighingFee !== originalWeighingFee) return true;
    if (displayOtherFee !== originalOtherFee) return true;
    if (selectedReopenDate !== (isViewingExisting ? selectedReopenDate : ""))
      return true;
    // Named other fees comparison
    if (otherFees.length !== originalOtherFees.length) return true;
    if (
      otherFees.some(
        (fee, i) =>
          fee.feeName !== (originalOtherFees[i]?.feeName || "") ||
          parseNumber(fee.feeAmount) !==
            parseNumber(originalOtherFees[i]?.feeAmount || 0),
      )
    )
      return true;
    return newItems.some((item, i) => {
      const o = items[i];
      return (
        parseNumber(item.unitPrice) !== parseNumber(o.unitPrice) ||
        parseNumber(item.quantity) !== parseNumber(o.quantity) ||
        parseNumber(item.moisture) !== parseNumber(o.moisture) ||
        parseNumber(item.netWeight) !== parseNumber(o.netWeight) ||
        parseNumber(item.discountItem) !== parseNumber(o.discountItem)
      );
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    items,
    newItems,
    vatPercent,
    originalVatPercent,
    selectedTaxRate,
    originalTaxRate,
    isSpecialTax,
    originalIsSpecialTax,
    displayWeighingFee,
    originalWeighingFee,
    displayOtherFee,
    originalOtherFee,
    otherFees,
    originalOtherFees,
  ]);

  const handleShowWeighingFee = () => {
    if (next.totalRaw <= 0) {
      swal({
        icon: "error",
        title: "No Products Added",
        text: "Please ensure products have valid quantities before adding a weighing fee.",
      });
      return;
    }
    setWeighingFeeModal(true);
  };

  // ── Named Other Fees helpers ───────────────────────────────────────────────
  const addNewFee = () => {
    const newFees = [...otherFees, { feeName: "", feeAmount: "" }];
    setOtherFees(newFees);
    setOtherFeePage(Math.floor((newFees.length - 1) / OTHER_FEE_PAGE_SIZE));
  };

  const handleFeeChange = (index, field, value) => {
    const newOtherFees = otherFees.map((fee, i) =>
      i === index ? { ...fee, [field]: value } : fee,
    );
    setOtherFees(newOtherFees);
  };

  const removeFee = (index) => {
    const newOtherFees = otherFees.filter((_, i) => i !== index);
    setOtherFees(newOtherFees);
    const newTotalPages = Math.ceil(newOtherFees.length / OTHER_FEE_PAGE_SIZE);
    if (otherFeePage >= newTotalPages && otherFeePage > 0) {
      setOtherFeePage((p) => p - 1);
    }
  };

  const handleAddWeighingFee = () => {
    const fee = parseNumber(inputWeighingFee);
    setDisplayWeighingFee(fee);
    setWeighingFeeModal(false);
  };

  const handleRemoveWeighingFee = (e) => {
    e.stopPropagation();
    setDisplayWeighingFee(0);
    setInputWeighingFee("");
  };

  const handleSpecialTax = (e) => {
    const isChecked = e.target.checked;
    setIsSpecialTax(isChecked);

    if (isChecked) {
      setBackupVatPercent(vatPercent);
      setBackupTaxId(selectedTaxId);
      setBackupTaxRate(selectedTaxRate);

      setVatPercent(0);
      setSelectedTaxId("");
      setSelectedTaxRate(0);
    } else {
      setVatPercent(backupVatPercent);
      setSelectedTaxId(backupTaxId);
      setSelectedTaxRate(backupTaxRate);
    }
  };

  const validateReopenDate = (reopenDate, invoiceDate) => {
    if (!reopenDate || !invoiceDate) return true;

    const reopen = new Date(reopenDate);
    const invoice = new Date(invoiceDate);

    // Compare year-month only
    const reopenYM = reopen.getFullYear() * 100 + (reopen.getMonth() + 1);
    const invoiceYM = invoice.getFullYear() * 100 + (invoice.getMonth() + 1);

    if (reopenYM <= invoiceYM) {
      swal({
        icon: "error",
        title: "Invalid Re-Open Date",
        text: `The Invoice Re-Open Date month (${reopen.toLocaleString("en-US", { month: "long", year: "numeric" })}) must be greater than the Invoice Date month (${invoice.toLocaleString("en-US", { month: "long", year: "numeric" })}).`,
      });
      setSelectedReopenDate("");
      return false;
    }
    return true;
  };

  // selectedReopenDate is kept as "yyyy-MM-dd" (what the API expects); the
  // DatePicker works with Date objects, so convert both ways here.
  const reopenDateValue = useMemo(() => {
    if (!selectedReopenDate) return null;
    const parsed = parseISO(String(selectedReopenDate));
    return isValidDateFns(parsed) ? parsed : null;
  }, [selectedReopenDate]);

  const handleReopenDateChange = (date) => {
    if (!date) {
      setSelectedReopenDate("");
      return;
    }
    const value = format(date, "yyyy-MM-dd");
    setSelectedReopenDate(value);
    if (validateReopenDate(value, reopenBasedInvoiceDate)) {
      warnIfNoCutoff(value, "Re-Open Date");
    }
  };

  const addReOpen = (e) => {
    e.preventDefault();
    setValidated(true);
    if (!hasChanges) return;
    if (!selectedReopenDate) {
      swal({
        icon: "error",
        title: "Re-Open Date Required",
        text: "Please select an Invoice Re-Open Date before proceeding.",
      });
      return;
    }
    if (!validateReopenDate(selectedReopenDate, reopenBasedInvoiceDate)) return;

    const baseItems = newItems.map((item) => ({
      stock_management_id: item.stockManagementId,
      unitPrice: parseNumber(item.unitPrice),
      averagePrice: parseNumber(item.averagePrice),
      quantity: parseNumber(item.quantity),
      moisture: parseNumber(item.moisture),
      netWeight: parseNumber(item.netWeight),
      discount: parseNumber(item.discountItem),
      discountType: item.discountType || "fixed",
      subtotal: parseNumber(item.subtotal).toFixed(2),
      salesProfit: 0,
    }));

    // ── EDIT MODE (existing re-open record) ──
    if (isViewingExisting) {
      swal({
        title: "Save changes?",
        text: "This will update the existing re-open invoice.",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      }).then((confirmed) => {
        if (!confirmed) return;

        axios
          .put(`${BASE_URL}/invoice/reOpenUpdate`, {
            id,
            userLoggedID,
            transactionDiscount: parseNumber(transactionDiscount),
            totalItemDiscount: parseNumber(itemDiscount),
            reOpenAmount: difference.toFixed(2),
            vatPercent: vatPercent,
            tax_id: selectedTaxId || null,
            tax_rate: selectedTaxRate || 0,
            isSpecialTax: isSpecialTax,
            weighingFee: displayWeighingFee || 0,
            otherFee: displayOtherFee || 0,
            otherFees: JSON.stringify(otherFees),
            selectedReopenDate,
            items: baseItems,
          })
          .then((res) => {
            if (res.status === 200) {
              swal({
                title: "Success",
                text: "Re-open invoice updated successfully",
                icon: "success",
                buttons: false,
                timer: 2000,
              }).then(() => {
                setOriginalVatPercent(vatPercent);
                setOriginalTaxRate(selectedTaxRate);
                setOriginalTaxId(selectedTaxId);
                setOriginalIsSpecialTax(isSpecialTax);
                setOriginalWeighingFee(displayWeighingFee);
                setOriginalOtherFee(displayOtherFee);
                setOriginalOtherFees(otherFees.map((f) => ({ ...f })));
                setEdit(false);
              });
            } else {
              swal({
                title: "Something Went Wrong",
                icon: "error",
                buttons: false,
                timer: 2000,
              });
            }
          })
          .catch(() => {
            swal({
              icon: "error",
              title: "Error",
              text: "Something went wrong. Please try again.",
            });
          });
      });

      return;
    }

    // ── CREATE MODE (no re-open record exists yet) ──
    swal({
      title: "Create re-open invoice?",
      text: "This will create a new re-open invoice and mark the original as re-opened.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((confirmed) => {
      if (!confirmed) return;

      axios
        .post(`${BASE_URL}/invoice/reOpenCreate`, {
          id,
          transactionId,
          selectedCustomer: customerId,
          selectedMethod: paymentMethod,
          selectedDueDate: selectedDueDate || null,
          inputPaymentTerms: paymentTerms || null,
          remarks,
          drNumber,
          poNumber,
          containerNumber,
          pier,
          userLoggedID,
          selectedDestination: destination,
          selectedCurrency: currencyId,
          selectedWarehouse: warehouseId,
          totalAmount: newTotal.toFixed(2),
          transactionDiscount: parseNumber(transactionDiscount),
          transactionDiscountType: "fixed",
          totalItemDiscount: parseNumber(itemDiscount),
          currencyRate: parseNumber(currencyRate),
          amount,
          qty,
          reOpenAmount: difference.toFixed(2),
          vatPercent,
          selectedReopenDate,
          tax_id: selectedTaxId || null,
          tax_rate: selectedTaxRate || 0,
          isSpecialTax: isSpecialTax,
          weighingFee: displayWeighingFee || 0,
          otherFee: displayOtherFee || 0,
          otherFees: JSON.stringify(otherFees),
          items: baseItems,
        })
        .then((res) => {
          if (res.status === 200) {
            swal({
              title: "Success",
              text: "Sales invoice re-open created successfully",
              icon: "success",
              buttons: false,
              timer: 2000,
            }).then(() => navigate(-1));
          } else {
            swal({
              title: "Something Went Wrong",
              icon: "error",
              buttons: false,
              timer: 2000,
            });
          }
        })
        .catch((error) => {
          if (error.response?.status === 409) {
            swal({
              icon: "error",
              title: "Transaction ID Conflict",
              text: "The transaction ID already exists. Please refresh and try again.",
            });
          } else {
            swal({
              icon: "error",
              title: "Error",
              text: "Something went wrong. Please try again.",
            });
          }
        });
    });
  };

  // ── Approve / Reject ──────────────────────────────────────────────────────
  const invoiceApproveReject = async (e, action) => {
    e.preventDefault();
    e.stopPropagation();

    let actualStatus;
    if (action === "approve") {
      actualStatus = newTotal === 0 ? "Paid" : "Approved";
    } else {
      actualStatus = "Rejected";
    }

    const message =
      action === "approve"
        ? newTotal === 0
          ? "Mark this invoice as Paid?"
          : "Approve this invoice?"
        : "Reject this invoice?";

    swal({
      title: message,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((confirmed) => {
      if (!confirmed) return;

      const currencyObj = currencyData.find((c) => c.id === selectedCurrency);
      const totalNetQty = newItems.reduce(
        (s, i) => s + parseNumber(i.netWeight),
        0,
      );
      const totalUnitPrice = newItems.reduce(
        (s, i) => s + parseNumber(i.unitPrice),
        0,
      );
      const averageUnitPrice = totalUnitPrice / (newItems.length || 1);

      axios
        .post(`${BASE_URL}/invoice/approveRejectInvoice`, {
          id,
          status: actualStatus,
          selectedInvoiceDate,
          selectedWarehouse,
          userLoggedID,
          shipmentFee: parseNumber(amount) * parseNumber(qty),
          items: newItems,
          transactionId: transactionIdDisplay,
          salesJournal: {
            customerId: customerId,
            date: selectedInvoiceDate,
            totalAmount: difference.toFixed(2),
            totalQuantity: totalNetQty,
            averageUnitPrice,
            paymentType: difference < 0 ? "Credit" : "Debit",
            // paymentType: "Debit",
            currencyName: currencyObj?.currency_name,
            currencyRate: currencyObj?.currency_rate,
          },
        })
        .then((res) => {
          if (res.status === 200) {
            const msg =
              actualStatus === "Paid"
                ? "Sales invoice has been marked as paid"
                : actualStatus === "Approved"
                  ? "Sales invoice has been approved"
                  : "Sales invoice has been rejected";
            swal({
              title: "Success",
              text: msg,
              icon: "success",
              buttons: false,
              timer: 2000,
            }).then(() => window.location.reload());
          } else if (res.status === 202) {
            const { invoiceDate, CutoffName, status } = res.data;
            swal({
              title: `${status === "Approved" ? "Approval" : "Rejection"} Declined`,
              text: `Invoice cannot be ${status} as its invoice date (${invoiceDate}) falls within the posted cutoff period named "${CutoffName}".`,
              icon: "warning",
            });
          }
        })
        .catch((err) => {
          if (err.response) {
            const status = err.response.status;
            const message = err.response.data?.message;

            if (status === 404) {
              swal({
                icon: "warning",
                title: "No Cutoff Found",
                text: message || "Cutoff not found or not yet created.",
              });
            } else {
              swal({
                icon: "error",
                title: "Error",
                text: message || "Something went wrong.",
              });
            }
          } else {
            swal({
              icon: "error",
              title: "Network Error",
              text: "Please check your connection.",
            });
          }
        });
    });
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const ArrowRow = ({ label, origVal, newVal, symbol = "" }) => {
    const color = arrowColor(origVal, newVal);
    const changed = parseNumber(origVal) !== parseNumber(newVal);
    return (
      <div className="w-100 d-flex flex-row justify-content-between align-items-center p-2">
        <span>{label}</span>
        <div className="d-flex align-items-center gap-2">
          <span className="text-secondary">
            {symbol}
            {formatToTwoDecimal(origVal)}
          </span>
          <i
            className="fa-solid fa-arrow-right text-muted"
            style={{ fontSize: "0.75rem" }}></i>
          <span style={{ color, fontWeight: changed ? 600 : 400 }}>
            {symbol}
            {formatToTwoDecimal(newVal)}
          </span>
        </div>
      </div>
    );
  };

  const editedStyle = (origVal, newVal) =>
    parseNumber(origVal) !== parseNumber(newVal)
      ? { border: "1.5px solid #f59e0b" }
      : {};

  // ── Loading state ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="h-100 w-100 border bg-white custom-container d-flex align-items-center justify-content-center">
        <div className="text-muted">Loading invoice...</div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="h-100 w-100 border bg-white custom-container">
      <style>{tableHoverCSS}</style>
      <Form noValidate validated={validated} onSubmit={addReOpen}>
        {/* ── Page header ── */}
        <div className="w-100 p-2 d-flex flex-row justify-content-between">
          <div className="d-flex flex-column title-custom">
            <span className="fs-3">
              <button
                type="button"
                className="border-0 bg-transparent d-inline-block"
                onClick={() =>
                  swal({
                    icon: "warning",
                    title: "Leave this page?",
                    text: "You will be redirected back.",
                    buttons: true,
                  }).then((c) => {
                    if (c) navigate(-1);
                  })
                }>
                <i className="fa-solid fa-arrow-left text-dark"></i>
              </button>
              SALES INVOICE RE-OPEN
            </span>
          </div>
        </div>

        {/* ── Header fields (all read-only / disabled) ── */}
        <div className="container-fluid mt-3">
          <div className="row mx-auto">
            <div className="col-sm mb-3">
              <span>Re-Open Transaction ID</span>
              <Form.Control
                type="text"
                className="form-control custom-form-height"
                value={isViewingExisting ? transactionIdDisplay : transactionId}
                readOnly
              />
            </div>
            <div className="col-sm mb-3">
              <span>Warehouse</span>
              <Form.Select
                className="form-select custom-form-height"
                value={warehouseId}
                disabled>
                <option value="" disabled>
                  Select Warehouse
                </option>
                {warehouseData.map((w, i) => (
                  <option key={i} value={w.warehouse_id}>
                    {w.name}
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>

          <div className="row mx-auto">
            <div className="col-sm mb-3">
              <span>Customer</span>
              <Form.Control
                type="text"
                className="form-control custom-form-height"
                value={customerLabel}
                readOnly
              />
            </div>
            <div className="col-sm mb-3">
              <span>Payment Method</span>
              <Form.Select
                className="form-select custom-form-height"
                value={paymentMethod}
                disabled>
                <option value="" disabled>
                  Select Payment Method
                </option>
                <option value="Cash">Cash</option>
                <option value="Bank">Bank</option>
                <option value="Wallet">Wallet</option>
              </Form.Select>
            </div>
          </div>

          <div className="row mx-auto">
            <div className="col-sm mb-3">
              <span>VAT %</span>
              <Form.Control
                type="text"
                inputMode="decimal"
                min="0"
                className="form-control custom-form-height"
                value={vatPercent}
                disabled={(isViewingExisting && !edit) || isSpecialTax}
                onChange={(e) => {
                  const value = e.target.value;

                  // Allow only numbers with max 1 decimal place
                  if (/^\d*\.?\d{0,1}$/.test(value)) {
                    setVatPercent(value);
                  }
                }}
              />
            </div>
            <div className="col-sm mb-3">
              <span>Withholding Tax</span>
              <Form.Select
                className="form-select custom-form-height"
                value={selectedTaxId}
                onChange={(e) => {
                  const selected = taxSettings.find(
                    (t) => String(t.id) === String(e.target.value),
                  );
                  const rate = selected ? selected.rate : 0;
                  setSelectedTaxId(e.target.value);
                  setSelectedTaxRate(rate);
                  setTaxName(selected?.name || "");
                }}
                disabled={(isViewingExisting && !edit) || isSpecialTax}>
                <option value="">None</option>
                {taxSettings.map((tax) => (
                  <option key={tax.id} value={tax.id}>
                    {tax.name} - {tax.rate}%
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>

          <div className="row mx-auto">
            <div className="col-sm mb-3">
              <span>
                Invoice Re-Open Date <span className="text-danger">*</span>
              </span>
              {/* Replaced by the typeable DatePicker below (TypedDateInput) */}
              {/* <Form.Control
                type="date"
                className="form-control custom-form-height"
                value={selectedReopenDate}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedReopenDate(value);
                  // validateReopenDate(value, reopenBasedInvoiceDate);
                  if (validateReopenDate(value, reopenBasedInvoiceDate)) {
                    warnIfNoCutoff(value, "Re-Open Date");
                  }
                }}
                // Open the calendar when clicking anywhere in the box, not
                // only on the calendar icon
                onClick={(e) => {
                  try {
                    e.currentTarget.showPicker?.();
                  } catch (error) {
                    // showPicker isn't allowed here (e.g. older browser) -
                    // the calendar icon still works
                  }
                }}
                style={
                  isViewingExisting && !edit ? undefined : { cursor: "pointer" }
                }
                disabled={isViewingExisting && !edit}
                required
              /> */}
              <DatePicker
                selected={reopenDateValue}
                onChange={handleReopenDateChange}
                customInput={<TypedDateInput />}
                className="form-control custom-form-height"
                wrapperClassName="w-100"
                placeholderText={DATE_INPUT_FORMAT}
                dateFormat={DATE_INPUT_FORMAT}
                strictParsing
                autoComplete="off"
                disabled={isViewingExisting && !edit}
                required
              />
            </div>
            <div className="col-sm mb-3">
              <span>
                Invoice Date <small className="text-muted">(Original)</small>
              </span>
              <Form.Control
                type="date"
                className="form-control custom-form-height"
                value={
                  reopenBasedInvoiceDate
                    ? new Date(reopenBasedInvoiceDate)
                        .toISOString()
                        .split("T")[0]
                    : ""
                }
                disabled
              />
            </div>
            <div className="col-sm mb-3">
              <span>Due Date</span>
              <Form.Control
                type="text"
                className="form-control custom-form-height"
                value={fmtDate(selectedDueDate)}
                readOnly
              />
            </div>
          </div>

          <div className="row mx-auto">
            <div className="col-sm mb-3 d-flex flex-row justify-content-between">
              <div className="w-50 me-3">
                <span>Destination</span>
                <Form.Select
                  className="form-select custom-form-height"
                  value={destination}
                  disabled>
                  <option value="" disabled>
                    Select Destination
                  </option>
                  <option value="Local">Local</option>
                  <option value="Overseas">Overseas</option>
                </Form.Select>
              </div>
              <div className="w-50">
                <span>Currency</span>
                <Form.Select
                  className="form-select custom-form-height"
                  value={currencyId}
                  disabled>
                  <option value="" disabled>
                    Select Currency
                  </option>
                  {currencyData.map((c, i) => (
                    <option key={i} value={c.id ?? c.currency_id}>
                      {c.currency_name} - {currencyList[c.currency_name]}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </div>
            <div className="col-sm mb-3">
              <div className="w-50 me-3 d-none">
                <Form.Control
                  type="text"
                  className="form-control custom-form-height"
                  value={currencyRate}
                  readOnly
                />
              </div>
              <div className="w-100">
                <span>Payment Terms</span>
                <Form.Control
                  type="text"
                  className="form-control custom-form-height"
                  value={paymentTerms}
                  disabled
                />
              </div>
            </div>
          </div>

          <div className="row mx-auto">
            <div className="col-sm mb-3 d-flex flex-row justify-content-between">
              <div className="w-50 me-3">
                <span>DR Number</span>
                <Form.Control
                  type="text"
                  className="form-control custom-form-height"
                  value={drNumber}
                  disabled
                />
              </div>
              <div className="w-50">
                <span>PO Number</span>
                <Form.Control
                  type="text"
                  className="form-control custom-form-height"
                  value={poNumber}
                  disabled
                />
              </div>
            </div>
          </div>

          <div className="row mx-auto">
            <div className="col-sm mb-3 d-flex flex-row justify-content-between">
              <div className="w-50 me-3">
                <span>Container Number</span>
                <Form.Control
                  type="text"
                  className="form-control custom-form-height"
                  value={containerNumber}
                  disabled
                />
              </div>
              <div className="w-50">
                <span>Pier</span>
                <Form.Control
                  type="text"
                  className="form-control custom-form-height"
                  value={pier}
                  disabled
                />
              </div>
            </div>
          </div>

          <div className="row mx-auto">
            <div className="col-sm mb-3">
              <span>Remarks</span>
              <Form.Control
                as="textarea"
                value={remarks}
                rows={3}
                maxLength={250}
                disabled
              />
            </div>
            <div className="col-sm mb-3 d-flex flex-row justify-content-between align-items-end">
              <div className="w-25">
                <span>Amount</span>
                <Form.Control
                  type="text"
                  className="form-control custom-form-height"
                  value={amount}
                  disabled
                />
              </div>
              <span className="align-self-center mb-2 mx-2">*</span>
              <div className="w-25">
                <span>Quantity</span>
                <Form.Control
                  type="text"
                  className="form-control custom-form-height"
                  value={qty}
                  disabled
                />
              </div>
              <span className="align-self-center mb-2 mx-2">=</span>
              <div className="w-50">
                <span>Shipment Amount</span>
                <Form.Control
                  type="text"
                  className="form-control custom-form-height"
                  value={formatToTwoDecimal(shipmentAmount)}
                  disabled
                />
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════
            ORIGINAL ITEM LIST — always fully disabled
        ══════════════════════════════════════════ */}
        <div className="container-fluid">
          <div className="w-100 d-flex align-items-center mt-3 p-2">
            <h5>Original Item List</h5>
            <hr className="flex-grow-1 mx-3" />
          </div>
          <div className="w-100 mt-2 p-2 new-item-custom scrollable-contents">
            <div className="table-responsive">
              <table className="table table-bordered table-hover item-list-table reopen-orig-table">
                <thead className="table-light">
                  <tr>
                    <th className="px-4 py-2 text-nowrap">Product Code</th>
                    <th className="px-4 py-2 text-nowrap">Product Name</th>
                    <th className="px-4 py-2 text-nowrap">Remaining</th>
                    <th className="px-4 py-2 text-nowrap">Unit Price</th>
                    <th className="px-4 py-2 text-nowrap">Qty</th>
                    <th className="px-4 py-2 text-nowrap">Moisture</th>
                    <th className="px-4 py-2 text-nowrap">Net Qty</th>
                    <th className="px-4 py-2 text-nowrap">Discount</th>
                    <th className="px-4 py-2 text-nowrap">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center text-muted py-3">
                        No items found.
                      </td>
                    </tr>
                  ) : (
                    items.map((item, index) => (
                      <tr key={item.id ?? index}>
                        <td>
                          <input
                            type="text"
                            className="form-control form-control-sm p-2"
                            value={item.productCode}
                            readOnly
                          />
                        </td>
                        <td>
                          <Select
                            options={[
                              {
                                value: item.productCode,
                                label: item.productName,
                              },
                            ]}
                            value={{
                              value: item.productCode,
                              label: item.productName,
                            }}
                            classNamePrefix="item-list-table-select"
                            styles={themeSelectStyles({})}
                            isDisabled
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            isSearchable={false}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-control form-control-sm p-2"
                            value={item.remaining}
                            readOnly
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-control form-control-sm p-2"
                            value={formatToTwoDecimal(item.unitPrice)}
                            disabled
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="text"
                            className="form-control form-control-sm p-2"
                            value={item.quantity}
                            disabled
                          />
                        </td>
                        <td>
                          <div className="input-group">
                            <Form.Control
                              type="text"
                              className="form-control form-control-sm p-2"
                              value={item.moisture}
                              disabled
                            />
                            <span className="input-group-text">%</span>
                          </div>
                        </td>
                        <td>
                          <Form.Control
                            type="text"
                            className="form-control form-control-sm p-2"
                            value={item.netWeight}
                            disabled
                          />
                        </td>
                        <td>
                          <div className="input-group mb-2">
                            <Form.Control
                              type="text"
                              className="form-control-sm p-2"
                              placeholder={
                                item.discountType === "percentage"
                                  ? "Discount (%)"
                                  : `Discount (${currencySymbol})`
                              }
                              value={item.discountItem}
                              disabled
                            />
                            <div className="input-group-prepend">
                              <button
                                className="input-group-text bg-white custom-discount-btn"
                                type="button"
                                disabled>
                                {item.discountType === "percentage"
                                  ? "%"
                                  : currencySymbol}
                              </button>
                            </div>
                          </div>
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-control form-control-sm p-2"
                            value={formatToTwoDecimal(item.subtotal)}
                            readOnly
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════
            NEW CHANGES — editable when edit === true
            Unit Price is now also editable
        ══════════════════════════════════════════ */}
        <div className="container-fluid">
          <div className="w-100 d-flex align-items-center mt-3 p-2">
            <h5>New Changes</h5>
            <hr className="flex-grow-1 mx-3" />
          </div>
          <div className="w-100 mt-2 p-2 new-item-custom scrollable-contents">
            <div className="table-responsive">
              <table className="table table-bordered table-hover item-list-table reopen-new-table">
                <thead className="table-light">
                  <tr>
                    <th className="px-4 py-2 text-nowrap">Product Code</th>
                    <th className="px-4 py-2 text-nowrap">Product Name</th>
                    <th className="px-4 py-2 text-nowrap">Remaining</th>
                    <th className="px-4 py-2 text-nowrap">Unit Price</th>
                    <th className="px-4 py-2 text-nowrap">Qty</th>
                    <th className="px-4 py-2 text-nowrap">Moisture</th>
                    <th className="px-4 py-2 text-nowrap">Net Qty</th>
                    <th className="px-4 py-2 text-nowrap">Discount</th>
                    <th className="px-4 py-2 text-nowrap">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {newItems.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center text-muted py-3">
                        No items found.
                      </td>
                    </tr>
                  ) : (
                    newItems.map((item, index) => {
                      const o = items[index] ?? {};
                      return (
                        <tr key={item.id ?? index}>
                          {/* Product Code */}
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm p-2"
                              value={item.productCode}
                              readOnly
                            />
                          </td>

                          {/* Product Name */}
                          <td>
                            <Select
                              options={[
                                {
                                  value: item.productCode,
                                  label: item.productName,
                                },
                              ]}
                              value={{
                                value: item.productCode,
                                label: item.productName,
                              }}
                              classNamePrefix="item-list-table-select"
                              styles={themeSelectStyles({})}
                              isDisabled
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              isSearchable={false}
                            />
                          </td>

                          {/* Remaining */}
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm p-2"
                              value={item.remaining}
                              readOnly
                            />
                          </td>

                          {/* Unit Price — now editable */}
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm p-2"
                              style={editedStyle(o.unitPrice, item.unitPrice)}
                              value={item.unitPrice}
                              onChange={(e) =>
                                updateNewItem(
                                  index,
                                  "unitPrice",
                                  e.target.value,
                                )
                              }
                              disabled={isViewingExisting && !edit}
                            />
                          </td>

                          {/* Qty */}
                          <td>
                            <Form.Control
                              type="text"
                              className="form-control form-control-sm p-2"
                              style={editedStyle(o.quantity, item.quantity)}
                              value={item.quantity}
                              onChange={(e) =>
                                updateNewItem(index, "quantity", e.target.value)
                              }
                              disabled={isViewingExisting && !edit}
                            />
                          </td>

                          {/* Moisture */}
                          <td>
                            <div className="input-group">
                              <Form.Control
                                type="text"
                                className="form-control form-control-sm p-2"
                                style={editedStyle(o.moisture, item.moisture)}
                                value={item.moisture}
                                onChange={(e) =>
                                  updateNewItem(
                                    index,
                                    "moisture",
                                    e.target.value,
                                  )
                                }
                                disabled={isViewingExisting && !edit}
                              />
                              <span className="input-group-text">%</span>
                            </div>
                          </td>

                          {/* Net Qty */}
                          <td>
                            <Form.Control
                              type="text"
                              className="form-control form-control-sm p-2"
                              style={editedStyle(o.netWeight, item.netWeight)}
                              value={item.netWeight}
                              onChange={(e) =>
                                updateNewItem(
                                  index,
                                  "netWeight",
                                  e.target.value,
                                )
                              }
                              disabled={isViewingExisting && !edit}
                            />
                          </td>

                          {/* Discount */}
                          <td>
                            <div className="input-group mb-2">
                              <Form.Control
                                type="text"
                                className="form-control-sm p-2"
                                style={editedStyle(
                                  o.discountItem,
                                  item.discountItem,
                                )}
                                placeholder={
                                  item.discountType === "percentage"
                                    ? "Discount (%)"
                                    : `Discount (${currencySymbol})`
                                }
                                value={item.discountItem}
                                onChange={(e) =>
                                  updateNewItem(
                                    index,
                                    "discountItem",
                                    e.target.value,
                                  )
                                }
                                disabled={isViewingExisting && !edit}
                              />
                              <div className="input-group-prepend">
                                <button
                                  className="input-group-text bg-white custom-discount-btn"
                                  type="button"
                                  disabled>
                                  {item.discountType === "percentage"
                                    ? "%"
                                    : currencySymbol}
                                </button>
                              </div>
                            </div>
                          </td>

                          {/* Subtotal (read-only, highlights on change) */}
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm p-2"
                              style={{
                                ...editedStyle(o.subtotal, item.subtotal),
                                color:
                                  parseNumber(o.subtotal) !==
                                  parseNumber(item.subtotal)
                                    ? arrowColor(o.subtotal, item.subtotal)
                                    : undefined,
                              }}
                              value={formatToTwoDecimal(item.subtotal)}
                              readOnly
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ── Totals summary ── */}
        <div className="row mx-auto p-2 mt-4 mb-2">
          <div className="col-sm"></div>
          <div className="col-sm border-end">
            <ArrowRow
              label="Total (QTY)"
              origVal={orig.totalQty}
              newVal={next.totalQty}
            />
            <ArrowRow
              label="Total Moisture (QTY)"
              origVal={orig.totalMoistQty}
              newVal={next.totalMoistQty}
            />
            <ArrowRow
              label="Total Net (QTY)"
              origVal={orig.totalNetQty}
              newVal={next.totalNetQty}
            />
          </div>
          <div className="col-sm">
            <ArrowRow
              label="Total Qty Amount"
              origVal={orig.totalRaw}
              newVal={next.totalRaw}
              symbol={`${currencySymbol} `}
            />
            <ArrowRow
              label="Total Moisture Amount"
              origVal={orig.moistAmount}
              newVal={next.moistAmount}
              symbol={`${currencySymbol} `}
            />
            <ArrowRow
              label="Total Net Qty Amount"
              origVal={orig.netAmount}
              newVal={next.netAmount}
              symbol={`${currencySymbol} `}
            />

            {/* Subtotal */}
            <ArrowRow
              label="Subtotal"
              origVal={origSubtotal}
              newVal={newSubtotal}
              symbol={`${currencySymbol} `}
            />

            {/* Discount */}
            <div className="w-100 d-flex flex-row justify-content-between align-items-center p-2">
              <span>Transaction Discount</span>
              <div className="d-flex align-items-center gap-1">
                <span className="text-secondary me-1">{currencySymbol}</span>
                <Form.Control
                  type="text"
                  className="form-control form-control-sm text-end"
                  style={{ maxWidth: "110px" }}
                  value={transactionDiscount}
                  onChange={(e) => setTransactionDiscount(e.target.value)}
                  disabled={isViewingExisting && !edit}
                />
              </div>
            </div>

            <div className="w-100 d-flex flex-row justify-content-between p-2">
              <span>Item Discount</span>
              <span className="text-secondary">
                {currencySymbol} {formatToTwoDecimal(itemDiscount)}
              </span>
            </div>

            {/* Gross Amount */}
            <div className="w-100 d-flex flex-row justify-content-between p-2">
              <span>Gross Amount</span>
              <span className="text-secondary">
                {currencySymbol} {formatToTwoDecimal(computedPreVatBase)}
              </span>
            </div>

            {/* VAT — green */}
            <div className="w-100 d-flex flex-row justify-content-between p-2">
              <span
                style={{
                  color:
                    !isSpecialTax && vatPercent > 0 ? "#198754" : undefined,
                }}>
                {!isSpecialTax && vatPercent > 0
                  ? `VAT (${vatPercent}%)`
                  : "Non-VAT"}
              </span>
              <span
                style={{
                  color:
                    !isSpecialTax && vatPercent > 0 ? "#198754" : undefined,
                }}>
                {currencySymbol}{" "}
                {!isSpecialTax && vatPercent > 0
                  ? formatToTwoDecimal(vatAmount)
                  : "0.00"}
              </span>
            </div>

            {/* Withholding Tax */}
            {!isSpecialTax && selectedTaxRate > 0 && (
              <div className="w-100 d-flex flex-row justify-content-between p-2">
                <span>
                  {taxName
                    ? `${taxName} (${selectedTaxRate}%)`
                    : `Withholding Tax (${selectedTaxRate}%)`}
                </span>
                <span className="text-danger">
                  ({currencySymbol} {formatToTwoDecimal(taxAmount)})
                </span>
              </div>
            )}

            {/* Special Tax Checkbox */}
            <div className="w-100 d-flex flex-row justify-content-between p-2 align-items-center">
              <Form.Check
                type="checkbox"
                id="special-tax-computation"
                label={
                  <span
                    title={`E = Gross Amount * 1.12\nL = Gross Amount * 0.12\nI = E * 0.01\nTax (Special Computation) = L - I`}
                    style={{ cursor: "pointer", userSelect: "none" }}>
                    Tax{" "}
                    <small className="text-muted">(Special Computation)</small>
                  </span>
                }
                className="mb-0"
                style={{ cursor: "pointer" }}
                checked={isSpecialTax}
                onChange={handleSpecialTax}
                disabled={isViewingExisting && !edit}
              />
              {isSpecialTax && (
                <span className="text-secondary text-success">
                  {currencySymbol}
                  {formatToTwoDecimal(specialTax)}
                </span>
              )}
            </div>

            {/* Weighing Fee (Non-Taxable) */}
            <div className="w-100 d-flex flex-row justify-content-between p-2">
              <span>
                Weighing Fee <small className="text-muted">(Non-Taxable)</small>
              </span>
              <span
                style={{
                  cursor:
                    !isViewingExisting || edit ? "pointer" : "not-allowed",
                  color: !isViewingExisting || edit ? "blue" : "gray",
                }}
                onClick={
                  !isViewingExisting || edit ? handleShowWeighingFee : undefined
                }>
                {displayWeighingFee > 0 ? (
                  <>
                    {currencySymbol} {formatToTwoDecimal(displayWeighingFee)}
                    <span
                      className="ms-2"
                      style={{
                        color: !isViewingExisting || edit ? "red" : "gray",
                        textDecoration:
                          !isViewingExisting || edit ? "underline" : "none",
                        cursor:
                          !isViewingExisting || edit
                            ? "pointer"
                            : "not-allowed",
                      }}
                      onClick={
                        !isViewingExisting || edit
                          ? handleRemoveWeighingFee
                          : null
                      }>
                      {!isViewingExisting || edit ? "Remove" : "Disabled"}
                    </span>
                  </>
                ) : !isViewingExisting || edit ? (
                  "Add"
                ) : (
                  "Disabled"
                )}
              </span>
            </div>

            {/* Named Other Fees (multi-row, paginated) */}
            {(() => {
              const isEditable = !isViewingExisting || edit;
              const start = otherFeePage * OTHER_FEE_PAGE_SIZE;
              const pageSlice = otherFees.slice(
                start,
                start + OTHER_FEE_PAGE_SIZE,
              );
              const totalPages = Math.ceil(
                otherFees.length / OTHER_FEE_PAGE_SIZE,
              );
              return (
                <>
                  {isEditable
                    ? pageSlice.map((fee, pageIndex) => {
                        const index = start + pageIndex;
                        return (
                          <div
                            key={index}
                            className="w-100 d-flex flex-row justify-content-between p-2 gap-1">
                            <Form.Control
                              type="text"
                              size="sm"
                              placeholder="Fee Name"
                              value={fee.feeName}
                              onChange={(e) =>
                                handleFeeChange(
                                  index,
                                  "feeName",
                                  e.target.value,
                                )
                              }
                            />
                            <NumberInDecimals
                              className="form-control form-control-sm text-danger"
                              placeholder="0.00"
                              decimals={2}
                              value={fee.feeAmount}
                              onChange={(val) =>
                                handleFeeChange(index, "feeAmount", val)
                              }
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeFee(index)}>
                              X
                            </button>
                          </div>
                        );
                      })
                    : pageSlice.map((fee, pageIndex) => {
                        const index = start + pageIndex;
                        return (
                          <div
                            key={index}
                            className="w-100 d-flex flex-row justify-content-between p-2">
                            <span className="text-muted">
                              {fee.feeName || `Fee ${index + 1}`}
                            </span>
                            <span className="text-danger">
                              {currencySymbol}
                              {formatToTwoDecimal(
                                parseFloat(fee.feeAmount) || 0,
                              )}
                            </span>
                          </div>
                        );
                      })}
                  {totalPages > 1 && (
                    <div className="w-100 d-flex justify-content-between align-items-center px-2 pb-1">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={otherFeePage === 0}
                        onClick={() => setOtherFeePage((p) => p - 1)}>
                        ‹ Prev
                      </button>
                      <small className="text-muted">
                        {otherFeePage + 1} / {totalPages}
                      </small>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={otherFeePage >= totalPages - 1}
                        onClick={() => setOtherFeePage((p) => p + 1)}>
                        Next ›
                      </button>
                    </div>
                  )}
                </>
              );
            })()}

            {/* Other Fee label + Add button */}
            <div className="w-100 d-flex flex-row justify-content-between p-2">
              <span>
                Other Fee <small className="text-muted">(Non-Taxable)</small>
              </span>
              {(!isViewingExisting || edit) && (
                <button
                  type="button"
                  onClick={addNewFee}
                  className="border-0 text-primary bg-white p-0">
                  Add
                </button>
              )}
            </div>

            {/* Total Amount */}
            <div className="w-100 d-flex flex-row justify-content-between p-3 mt-3 total-amount-container align-items-center rounded">
              <span className="text-white">Total Amount</span>
              <div className="d-flex align-items-center gap-2">
                <span className="text-white" style={{ opacity: 0.65 }}>
                  {currencySymbol} {formatToTwoDecimal(origTotal)}
                </span>
                <i
                  className="fa-solid fa-arrow-right text-white"
                  style={{ fontSize: "0.75rem", opacity: 0.65 }}></i>
                <span
                  style={{
                    color:
                      difference === 0
                        ? "#fff"
                        : difference > 0
                          ? "#86efac"
                          : "#fca5a5",
                    fontWeight: 700,
                  }}>
                  {currencySymbol} {formatToTwoDecimal(newTotal)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Summary of Changes card ── */}
        <div className="container-fluid mb-4 mt-2">
          <div
            className="border rounded p-3"
            style={{ backgroundColor: "#f8f9fa" }}>
            <h6 className="fw-bold mb-3">Summary of Changes</h6>
            <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
              <span>Original Total Amount:</span>
              <span className="fw-semibold text-primary">
                {currencySymbol} {formatToTwoDecimal(origTotal)}
              </span>
            </div>
            <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
              <span>New Total Amount:</span>
              <span className="fw-semibold" style={{ color: "#f59e0b" }}>
                {currencySymbol} {formatToTwoDecimal(newTotal)}
              </span>
            </div>
            <div className="d-flex justify-content-between align-items-center py-2 mt-1">
              <span className="fw-bold fs-6">Difference:</span>
              <span
                className="fw-bold fs-6"
                style={{
                  color:
                    difference > 0
                      ? "#3B9F3F"
                      : difference < 0
                        ? "#dc3545"
                        : "#6c757d",
                }}>
                {difference > 0 ? "+" : difference < 0 ? "-" : ""}
                {currencySymbol} {formatToTwoDecimal(Math.abs(difference))}
              </span>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════
            ACTION BUTTONS
            ─────────────────────────────────────────
            CREATE MODE  (!isViewingExisting)
              → Cancel  |  Save Changes (disabled until hasChanges)

            VIEW MODE  (isViewingExisting && !edit)
              → Cancel  |  Edit  |  Preview PDF

            EDIT MODE  (isViewingExisting && edit)
              → Cancel  |  Save Changes (disabled until hasChanges)
        ══════════════════════════════════════════ */}
        <div className="container-fluid mt-3 mb-4">
          <div className="row">
            <div className="col-sm"></div>
            <div className="col-sm"></div>
            <div className="col-sm">
              <div className="row">
                <div className="col-sm"></div>

                {/* ── CREATE MODE ── */}
                {!isViewingExisting && (
                  <>
                    <div className="col-sm mb-2">
                      <button
                        className="btn btn-secondary w-100"
                        type="button"
                        onClick={() =>
                          swal({
                            icon: "warning",
                            title: "Leave this page?",
                            text: "You will be redirected back.",
                            dangerMode: true,
                            buttons: true,
                          }).then((c) => {
                            if (c) navigate(-1);
                          })
                        }>
                        Cancel
                      </button>
                    </div>
                    <div className="col-sm mb-2">
                      <button
                        className="btn btn-primary w-100"
                        type="submit"
                        disabled={!hasChanges}
                        title={
                          !hasChanges ? "Change at least one item to save" : ""
                        }>
                        Save Changes
                      </button>
                    </div>
                  </>
                )}

                {/* ── VIEW MODE (existing re-open, not currently editing) ── */}
                {isViewingExisting && !edit && (
                  <>
                    <div className="col-sm mb-2">
                      <button
                        className="btn btn-secondary w-100"
                        type="button"
                        onClick={() =>
                          swal({
                            icon: "warning",
                            title: "Leave this page?",
                            text: "You will be redirected back.",
                            dangerMode: true,
                            buttons: true,
                          }).then((c) => {
                            if (c) navigate(-1);
                          })
                        }>
                        Cancel
                      </button>
                    </div>

                    {invoiceStatus === "Pending" && (
                      <div className="col-sm mb-2">
                        <button
                          className="btn btn-primary w-100"
                          type="button"
                          onClick={() => setEdit(true)}>
                          Edit
                        </button>
                      </div>
                    )}

                    <div className="col-sm mb-2">
                      <button
                        className="btn btn-outline-danger w-100"
                        type="button"
                        onClick={openPDFPreview}>
                        Preview PDF
                      </button>
                    </div>
                  </>
                )}

                {/* ── EDIT MODE (existing re-open, actively editing) ── */}
                {isViewingExisting && edit && (
                  <>
                    <div className="col-sm mb-2">
                      <button
                        className="btn btn-secondary w-100"
                        type="button"
                        onClick={() =>
                          swal({
                            icon: "warning",
                            title: "Discard changes?",
                            text: "Your edits will not be saved.",
                            dangerMode: true,
                            buttons: true,
                          }).then((confirmed) => {
                            if (confirmed) {
                              // Reset editable fields back to their original saved values
                              setDisplayWeighingFee(originalWeighingFee);
                              setDisplayOtherFee(originalOtherFee);
                              setInputWeighingFee("");
                              setOtherFees(
                                originalOtherFees.map((f) => ({ ...f })),
                              );
                              setSelectedInvoiceDate(originalInvoiceDate);
                              setVatPercent(originalVatPercent);
                              setSelectedTaxRate(originalTaxRate);
                              setSelectedTaxId(originalTaxId);
                              setIsSpecialTax(originalIsSpecialTax);
                              setNewItems(items.map((i) => ({ ...i })));
                              setEdit(false);
                            }
                          })
                        }>
                        Cancel
                      </button>
                    </div>
                    <div className="col-sm mb-2">
                      <button
                        className="btn btn-primary w-100"
                        type="submit"
                        disabled={!hasChanges}
                        title={
                          !hasChanges ? "Change at least one item to save" : ""
                        }>
                        Save Changes
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Weighing Fee Modal */}
        <Modal
          show={weighingFeeModal}
          onHide={() => setWeighingFeeModal(false)}
          size="md">
          <Form>
            <Modal.Header closeButton style={{ border: "none" }}>
              <Modal.Title>Weighing Fee (Non-Taxable)</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p className="fs-6">
                This fee is non-taxable and will not affect VAT or Withholding
                Tax calculations.
              </p>
              <Form.Control
                type="text"
                className="form-control-sm p-2"
                value={inputWeighingFee}
                placeholder={`Amount (${currencySymbol})`}
                onChange={(e) => {
                  let v = e.target.value.replace(/[^0-9.]/g, "");
                  if ((v.match(/\./g) || []).length > 1) return;
                  setInputWeighingFee(v);
                }}
              />
            </Modal.Body>
            <Modal.Footer style={{ border: "none" }}>
              <Button
                variant="outline-secondary"
                onClick={() => setWeighingFeeModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleAddWeighingFee}>
                Add
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* ── PDF Preview Modal ── */}
        <Modal show={print} size="xl" onHide={() => setPrint(false)}>
          <div className="position-relative">
            <a
              href={invoicePdf.url || undefined}
              download={`Sales Invoice - ${transactionIdDisplay}.pdf`}>
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip style={{ fontSize: "0.8rem" }}>
                    Download as{" "}
                    <strong className="text-danger">
                      "Sales Invoice - {transactionIdDisplay}.pdf"
                    </strong>
                  </Tooltip>
                }
                delay={300}>
                <Button
                  variant="light"
                  className="position-absolute btn btn-light border border-secondary-subtle rounded-5"
                  style={{ bottom: "1rem", left: "1rem" }}
                  disabled={!invoicePdf.url}>
                  <i className="fa-solid fa-download"></i>
                </Button>
              </OverlayTrigger>
            </a>

            <div
              className="position-absolute"
              style={{ bottom: "1.5rem", right: "2.5rem" }}>
              {invoiceStatus === "Pending" ? (
                <div className="d-flex gap-1">
                  <button
                    className="btn btn-sm btn-danger px-3"
                    type="button"
                    onClick={(e) => invoiceApproveReject(e, "reject")}
                    disabled={isCutoffPosted || !isCutoffExists}>
                    Reject
                  </button>
                  <button
                    className="btn btn-sm btn-success"
                    type="button"
                    onClick={(e) => invoiceApproveReject(e, "approve")}
                    disabled={isCutoffPosted || !isCutoffExists}>
                    Approve
                  </button>
                </div>
              ) : (
                <span
                  className={`badge px-3 py-2 fs-6 ${
                    invoiceStatus === "Approved" || invoiceStatus === "Paid"
                      ? "bg-success-subtle text-success border border-success-subtle"
                      : "bg-danger-subtle text-danger border border-danger-subtle"
                  }`}>
                  {invoiceStatus}
                </span>
              )}
            </div>

            <InvoicePDFFrame url={invoicePdf.url} error={invoicePdf.error} />
          </div>
        </Modal>
      </Form>
    </div>
  );
};

export default InvoiceUpdateReOpen;
